import {
  Box,
  createStyles,
  PasswordInput,
  PasswordInputProps,
  Progress,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import React, { useState } from 'react';
import { ref, string } from 'yup';

import { ReactComponent as CloseCircle } from '@portals/icons/linear/close-circle.svg';
import { ReactComponent as TickCircle } from '@portals/icons/linear/tick-circle.svg';

interface PasswordRequirementProps {
  meets: boolean;
  label: string;
}

function PasswordRequirement({ meets, label }: PasswordRequirementProps) {
  return (
    <Text
      color={meets ? 'teal' : 'red'}
      sx={{ display: 'flex', alignItems: 'center' }}
      mt={7}
      size="sm"
    >
      {meets ? (
        <TickCircle width={14} height={14} />
      ) : (
        <CloseCircle width={14} height={14} />
      )}{' '}
      <Box ml={10}>{label}</Box>
    </Text>
  );
}

const requirements = [
  { regex: /[0-9]/, label: 'Must include a number' },
  { regex: /[A-Z]/, label: 'Must include an uppercase letter' },
  { regex: /.{10,100}/, label: 'Must contain between 10 and 100 characters' },
  {
    regex: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>]/,
    label: 'Must include a special character',
  },
] as const;

function getStrength(password: string) {
  let multiplier = password.length > 9 ? 0 : 1;

  requirements.forEach((requirement) => {
    if (!requirement.regex.test(password)) {
      multiplier += 1;
    }
  });

  return Math.max(100 - (100 / (requirements.length + 1)) * multiplier, 10);
}

interface PasswordInputWithRequirementsProps {
  label?: PasswordInputProps['label'];
  placeholder?: PasswordInputProps['placeholder'];
  value: string;
  onChange: PasswordInputProps['onChange'];
  error: PasswordInputProps['error'];
}

export function PasswordInputWithRequirements({
  label = 'Password',
  placeholder = 'Password',
  value,
  onChange,
  error,
}: PasswordInputWithRequirementsProps) {
  const { classes } = useStyles();
  const [isOpen, setIsOpen] = useState(false);

  const strength = getStrength(value);
  const color = strength === 100 ? 'teal' : strength > 50 ? 'yellow' : 'red';

  return (
    <Tooltip
      opened={isOpen}
      withinPortal
      withArrow
      arrowSize={10}
      position="bottom"
      classNames={{
        tooltip: classes.tooltip,
      }}
      label={
        <Stack spacing={4} p="sm">
          <Progress color={color} value={strength} size={5} mb={10} />

          {requirements.map((requirement, index) => (
            <PasswordRequirement
              key={index}
              label={requirement.label}
              meets={requirement.regex.test(value)}
            />
          ))}
        </Stack>
      }
    >
      <PasswordInput
        required
        error={error}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        data-testid="password-input"
        onFocusCapture={() => setIsOpen(true)}
        onBlurCapture={() => setIsOpen(false)}
      />
    </Tooltip>
  );
}

export const yupPasswordValidator = string()
  .required()
  .matches(requirements[3].regex, requirements[3].label)
  .matches(requirements[2].regex, requirements[2].label)
  .matches(requirements[1].regex, requirements[1].label)
  .matches(requirements[0].regex, requirements[0].label);

export function yupPasswordConfirmValidator(passwordFieldName: string) {
  return string()
    .required()
    .oneOf([ref(passwordFieldName), null], 'Passwords must match');
}

const useStyles = createStyles((theme) => ({
  tooltip: {
    pointerEvents: 'all',
    background: 'white',
    filter: 'drop-shadow(-4px 4px 36px rgba(0, 2, 41, 0.1))',
  },
}));
